import { useMemo, useState } from 'react';
import { Button, Card, Select, Space, Typography, Steps, theme, App, Tabs, Table, Tag, Modal, Checkbox } from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  DatabaseOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { trpc, trpcUntypedClient } from '../lib/api';
import { PageHeader, PageContainer, DevFilePathBadge } from '../components/common';
import { DateDisplay } from '../components/common/DateDisplay';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

type MasterDataType = 'locations' | 'equipments' | 'parts' | 'suppliers';
type ExportCategory = 'documents' | 'equipments' | 'parts' | 'suppliers' | 'locations';

const MASTER_DATA_LABELS: Record<MasterDataType, string> = {
  locations: 'エリア',
  equipments: '設備',
  parts: '部品・消耗品',
  suppliers: '業者',
};

const FILE_EXPORT_LABELS: Record<ExportCategory, string> = {
  documents: '書類',
  equipments: '設備',
  parts: '部品・消耗品',
  suppliers: '業者',
  locations: 'エリア',
};

interface ExportCountResult {
  count: number;
}

export function ExportPage() {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [exportType, setExportType] = useState<MasterDataType>('locations');
  const [currentStep, setCurrentStep] = useState(0);
  const [exportCount, setExportCount] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [isCheckingCount, setIsCheckingCount] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<ExportCategory[]>(['documents']);
  const [downloadJobId, setDownloadJobId] = useState<string | null>(null);

  const { data: categoryStats } = trpc.documentExport.categoryStats.useQuery();
  const { data: exportJobs, refetch: refetchExportJobs } = trpc.documentExport.list.useQuery(
    undefined,
    { refetchInterval: 10000 }
  );
  const { data: downloadUrls, isLoading: downloadUrlsLoading } = trpc.documentExport.listUrls.useQuery(
    { jobId: downloadJobId ?? '' },
    { enabled: Boolean(downloadJobId) }
  );

  const fileExportMutation = trpc.documentExport.create.useMutation({
    onSuccess: () => {
      message.success('ファイルエクスポートを開始しました');
      void refetchExportJobs();
    },
    onError: (error) => {
      message.error(`エクスポートエラー: ${error.message}`);
    },
  });

  // エクスポート実行
  const exportMutation = trpc.import.exportMasterData.useMutation({
    onSuccess: (data) => {
      // Base64デコードしてBlobに変換
      const binaryString = window.atob(data.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'text/csv;charset=shift_jis;' });

      // ダウンロード
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', data.filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setCurrentStep(2);
      message.success(`${data.recordCount}件のデータをエクスポートしました`);
      setExporting(false);
    },
    onError: (error) => {
      message.error(`エクスポートエラー: ${error.message}`);
      setExporting(false);
    },
  });

  const handleCheckCount = async () => {
    setIsCheckingCount(true);
    try {
      const data = await trpcUntypedClient.query('import.getExportCount', {
        type: exportType,
        filters: {},
      }) as ExportCountResult;
      setExportCount(data.count);
      setCurrentStep(1);
      if (data.count === 0) {
        message.warning('エクスポートするデータがありません');
      } else {
        message.success(`${data.count}件のデータが見つかりました`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '件数取得に失敗しました';
      message.error(`エラー: ${errorMessage}`);
    } finally {
      setIsCheckingCount(false);
    }
  };

  const handleExport = () => {
    setExporting(true);
    exportMutation.mutate({
      type: exportType,
      filters: {},
    });
  };

  const handleReset = () => {
    setCurrentStep(0);
    setExportCount(null);
    setExporting(false);
  };

  const steps = [
    {
      title: 'データ選択',
      icon: <FileTextOutlined />,
    },
    {
      title: '件数確認',
      icon: <InfoCircleOutlined />,
    },
    {
      title: 'エクスポート完了',
      icon: <CheckCircleOutlined />,
    },
  ];

  const formatBytes = (bytes: number) => {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    const precision = unitIndex <= 1 ? 0 : unitIndex === 2 ? 1 : 2;
    return `${value.toFixed(precision)} ${units[unitIndex]}`;
  };

  const statsByCategory = useMemo(() => {
    const map = new Map<ExportCategory, { fileCount: number; totalBytes: number }>();
    (categoryStats?.items ?? []).forEach((item: { categoryKey: ExportCategory; fileCount: number; totalBytes: number }) => {
      map.set(item.categoryKey, { fileCount: item.fileCount, totalBytes: item.totalBytes });
    });
    return map;
  }, [categoryStats]);

  const renderCsvExport = () => (
    <>
      <Steps current={currentStep} items={steps} style={{ marginBottom: 32 }} />

      {currentStep === 0 && (
        <Card title="エクスポートするデータを選択">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong>データタイプ</Text>
              <Select
                value={exportType}
                onChange={(value) => setExportType(value)}
                style={{ width: '100%', marginTop: 8 }}
                size="large"
              >
                <Option value="locations">
                  <DatabaseOutlined /> {MASTER_DATA_LABELS.locations}
                </Option>
                <Option value="equipments">
                  <DatabaseOutlined /> {MASTER_DATA_LABELS.equipments}
                </Option>
                <Option value="parts">
                  <DatabaseOutlined /> {MASTER_DATA_LABELS.parts}
                </Option>
                <Option value="suppliers">
                  <DatabaseOutlined /> {MASTER_DATA_LABELS.suppliers}
                </Option>
              </Select>
            </div>

            <div style={{
              background: token.colorInfoBg,
              padding: '16px',
              borderRadius: '4px',
              border: `1px solid ${token.colorInfoBorder}`
            }}>
              <Space direction="vertical">
                <Text strong>
                  <InfoCircleOutlined style={{ color: token.colorInfo }} /> エクスポート形式
                </Text>
                <Text type="secondary">
                  ? ファイル形式: CSV（カンマ区切り）
                </Text>
                <Text type="secondary">
                  ? 文字コード: Shift-JIS
                </Text>
                <Text type="secondary">
                  ? ヘッダー行: 日本語フィールド名
                </Text>
              </Space>
            </div>

            <Button
              type="primary"
              size="large"
              icon={<InfoCircleOutlined />}
              onClick={handleCheckCount}
              loading={isCheckingCount}
              block
            >
              エクスポート件数を確認
            </Button>
          </Space>
        </Card>
      )}

      {currentStep === 1 && exportCount !== null && (
        <Card title="エクスポート件数の確認">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <Title level={3} style={{ marginBottom: 8 }}>
                {MASTER_DATA_LABELS[exportType]}
              </Title>
              <div style={{
                fontSize: '48px',
                fontWeight: 'bold',
                color: exportCount > 0 ? token.colorSuccess : token.colorWarning,
                marginBottom: 16
              }}>
                {exportCount.toLocaleString()}
              </div>
              <Text type="secondary" style={{ fontSize: '16px' }}>
                件のデータがエクスポート可能です
              </Text>
            </div>

            {exportCount > 0 ? (
              <div style={{
                background: token.colorSuccessBg,
                padding: '16px',
                borderRadius: '4px',
                border: `1px solid ${token.colorSuccessBorder}`
              }}>
                <Space direction="vertical">
                  <Text strong>
                    <CheckCircleOutlined style={{ color: token.colorSuccess }} /> エクスポート準備完了
                  </Text>
                  <Text type="secondary">
                    このデータをCSVファイルとしてダウンロードします。
                  </Text>
                </Space>
              </div>
            ) : (
              <div style={{
                background: token.colorWarningBg,
                padding: '16px',
                borderRadius: '4px',
                border: `1px solid ${token.colorWarningBorder}`
              }}>
                <Space direction="vertical">
                  <Text strong>
                    <InfoCircleOutlined style={{ color: token.colorWarning }} /> データがありません
                  </Text>
                  <Text type="secondary">
                    エクスポートできるデータが見つかりませんでした。
                  </Text>
                </Space>
              </div>
            )}

            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={handleReset}>
                戻る
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={exporting}
                disabled={exportCount === 0}
              >
                CSVファイルをダウンロード
              </Button>
            </Space>
          </Space>
        </Card>
      )}

      {currentStep === 2 && (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <CheckCircleOutlined
              style={{ fontSize: '72px', color: token.colorSuccess, marginBottom: 24 }}
            />
            <Title level={3}>エクスポート完了</Title>
            <Paragraph style={{ fontSize: '16px', marginBottom: 32 }}>
              {MASTER_DATA_LABELS[exportType]}データのエクスポートが完了しました。
            </Paragraph>
            <Space>
              <Button size="large" onClick={handleReset}>
                続けてエクスポート
              </Button>
            </Space>
          </div>
        </Card>
      )}
    </>
  );

  const columns = [
    {
      title: '作成日時',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => <DateDisplay value={value} />,
      width: 180,
    },
    {
      title: 'カテゴリ',
      dataIndex: 'paramsJson',
      key: 'categories',
      render: (params: { categories?: ExportCategory[] }) => (
        <Space wrap>
          {(params?.categories ?? []).map((category) => (
            <Tag key={category}>{FILE_EXPORT_LABELS[category] ?? category}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (value: string) => {
        const color = value === 'complete'
          ? 'green'
          : value === 'failed'
            ? 'red'
            : value === 'running'
              ? 'blue'
              : 'default';
        return <Tag color={color}>{value}</Tag>;
      },
      width: 120,
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_: unknown, record: { id: string; status: string }) => (
        <Space>
          <Button
            type="link"
            icon={<FolderOpenOutlined />}
            onClick={() => setDownloadJobId(record.id)}
            disabled={record.status !== 'complete'}
          >
            ZIP一覧
          </Button>
        </Space>
      ),
    },
  ];

  const renderFileExport = () => (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="エクスポート対象カテゴリ">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text strong>カテゴリ選択</Text>
          <Checkbox.Group
            value={selectedCategories}
            onChange={(values) => setSelectedCategories(values as ExportCategory[])}
            style={{ width: '100%' }}
          >
            <Space wrap>
              {(Object.keys(FILE_EXPORT_LABELS) as ExportCategory[]).map((category) => (
                <Checkbox key={category} value={category}>
                  <Space size={6}>
                    <span>{FILE_EXPORT_LABELS[category]}</span>
                    <Text type="secondary">
                      {(() => {
                        const stats = statsByCategory.get(category);
                        const fileCount = stats?.fileCount ?? 0;
                        const totalBytes = stats?.totalBytes ?? 0;
                        return `(${fileCount.toLocaleString()}件 / ${formatBytes(totalBytes)})`;
                      })()}
                    </Text>
                  </Space>
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => fileExportMutation.mutate({ categories: selectedCategories })}
            disabled={selectedCategories.length === 0}
            loading={fileExportMutation.isPending}
          >
            ファイルエクスポートを開始
          </Button>
        </Space>
      </Card>

      <Card title="エクスポート履歴">
        <Table
          rowKey="id"
          dataSource={exportJobs?.items ?? []}
          columns={columns}
          pagination={false}
          size="small"
        />
      </Card>

      <Modal
        title="ZIPダウンロード"
        open={Boolean(downloadJobId)}
        onCancel={() => setDownloadJobId(null)}
        footer={null}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {downloadUrlsLoading && <Text type="secondary">読み込み中...</Text>}
          {!downloadUrlsLoading && (downloadUrls?.items ?? []).length === 0 && (
            <Text type="secondary">ダウンロード可能なZIPがありません</Text>
          )}
          {(downloadUrls?.items ?? []).map((item: { batchId: string; url: string; categoryKey: string; fileCount: number }) => (
            <Card key={item.batchId} size="small">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong>{FILE_EXPORT_LABELS[item.categoryKey as ExportCategory] ?? item.categoryKey}</Text>
                <Text type="secondary">ファイル数: {item.fileCount}</Text>
                <Button type="primary" href={item.url} target="_blank" rel="noreferrer">
                  ZIPをダウンロード
                </Button>
              </Space>
            </Card>
          ))}
        </Space>
      </Modal>
    </Space>
  );

  return (
    <PageContainer>
      <DevFilePathBadge filePath="client/src/pages/ExportPage.tsx" />
      <PageHeader
        title="データエクスポート"
        subtitle="CSVとファイルのエクスポートを実行します（管理者のみ）"
      />

      <Tabs
        defaultActiveKey="csv"
        items={[
          {
            key: 'csv',
            label: 'マスターデータ（CSV）',
            children: renderCsvExport(),
          },
          {
            key: 'file',
            label: 'ファイル（ZIP）',
            children: renderFileExport(),
          },
        ]}
      />
    </PageContainer>
  );
}
